import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// The gear sheet for a one-shot or a series: who writes, who edits, and
// links to the story bible and anything else the page passes in. A model
// change applies to everything written after it; what's already written stays.

export const GearIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
  </svg>
);
const BookIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z" /><path d="M4 19V5" /><path d="M9 7h6" />
  </svg>
);
const ChevronIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
);

export default function WritingSettings({ work, onClose, onSaved, onOpenBible, children }) {
  const token = localStorage.getItem('token');
  const [models, setModels] = useState([]);
  const [writer, setWriter] = useState(work.writer_model);
  const [editor, setEditor] = useState(work.editor_model);
  const [saved, setSaved] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/translation-models?token=${token}`)
      .then(res => setModels(res.data.models || []))
      .catch(() => setErrorMsg("couldn't load the model list"));
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const save = (which, id) => {
    const prevW = writer; const prevE = editor;
    if (which === 'writer') setWriter(id); else setEditor(id);
    setSaved(''); setErrorMsg('');
    axios.put(`${API}/writing/works/${work.id}/models`, { token, [`${which}_model`]: id })
      .then(res => {
        setSaved(which);
        try { localStorage.setItem(which === 'writer' ? 'writerModel' : 'editorModel', id); } catch (e) { /* fine */ }
        onSaved && onSaved(res.data.work);
        setTimeout(() => setSaved(''), 1800);
      })
      .catch(err => { setWriter(prevW); setEditor(prevE); setErrorMsg(err?.response?.data?.detail || "couldn't change it"); });
  };

  // keep the current model selectable even if it was taken off the list
  const options = (current) => (models.some(m => m.id === current) || !current ? models : [{ id: current, label: current }, ...models]);

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.sheet} onClick={e => e.stopPropagation()} role="dialog" aria-label="settings">
        <div style={s.head}>
          <div style={s.title}>settings</div>
          <button className="wr-btn" style={s.close} onClick={onClose} aria-label="close">×</button>
        </div>

        <div style={s.group}>
          <div style={s.label}>who writes it</div>
          <div style={s.field}>
            <label style={s.fieldName} htmlFor="ws-writer">writer {saved === 'writer' && <span style={s.saved}>saved</span>}</label>
            <select id="ws-writer" style={s.select} value={writer} onChange={e => save('writer', e.target.value)}>
              {options(writer).map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div style={s.field}>
            <label style={s.fieldName} htmlFor="ws-editor">editor {saved === 'editor' && <span style={s.saved}>saved</span>}</label>
            <select id="ws-editor" style={s.select} value={editor} onChange={e => save('editor', e.target.value)}>
              {options(editor).map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div style={s.hint}>
            {work.kind === 'series' ? 'for the whole series. ' : ''}applies to everything from now on — what's already written stays as it is.
          </div>
          {errorMsg && <div style={s.error}>{errorMsg}</div>}
        </div>

        <button className="wr-btn" style={s.row} onClick={onOpenBible}>
          <BookIcon />
          <span style={{ flex: 1, textAlign: 'left' }}>
            story bible
            <span style={s.rowSub}>{work.bible_count || 0} things it keeps true{work.suggestions ? ` · ${work.suggestions} waiting for you` : ''}</span>
          </span>
          <ChevronIcon />
        </button>

        {children}

        {work.cost_usd > 0 && <div style={{ ...s.hint, textAlign: 'center', marginTop: '4px' }}>so far this has cost ${Number(work.cost_usd).toFixed(work.cost_usd < 1 ? 3 : 2)}</div>}
      </div>
    </div>
  );
}

const sans = "'DM Sans', sans-serif";
const s = {
  overlay: { position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(8,6,10,0.72)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  sheet: { width: '100%', maxWidth: '520px', maxHeight: '88vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '22px 22px 0 0', padding: '18px 18px calc(24px + env(safe-area-inset-bottom))', boxSizing: 'border-box', boxShadow: '0 -10px 40px rgba(0,0,0,0.5)', fontFamily: sans, color: '#e8ddd0', fontWeight: 300 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', fontStyle: 'italic' },
  close: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '20px', cursor: 'pointer' },
  group: { display: 'flex', flexDirection: 'column', gap: '10px', padding: '14px', borderRadius: '16px', background: '#17131b' },
  label: { fontSize: '10px', color: '#8a7466', letterSpacing: '0.12em', textTransform: 'uppercase' },
  field: { display: 'flex', flexDirection: 'column', gap: '5px' },
  fieldName: { fontSize: '12.5px', color: '#c9a57a', display: 'flex', alignItems: 'center', gap: '8px' },
  saved: { fontSize: '11px', color: '#7fa37a' },
  select: { width: '100%', boxSizing: 'border-box', background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.18)', borderRadius: '12px', padding: '11px 13px', color: '#e8ddd0', fontSize: '14px', fontFamily: sans, outline: 'none', colorScheme: 'dark' },
  hint: { fontSize: '12px', color: '#7a6a5a', lineHeight: 1.55 },
  error: { fontSize: '12px', color: '#e0a0a0' },
  row: { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', borderRadius: '16px', border: 'none', background: '#17131b', color: '#e0d4c6', fontSize: '14px', cursor: 'pointer', fontFamily: sans },
  rowSub: { display: 'block', fontSize: '11.5px', color: '#7a6a5a', marginTop: '2px' },
};
